import React, { useEffect, useState } from "react";
import {
  Box,
  Skeleton,
  Stack,
  SimpleGrid,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import NoOfEmployeeTab from "../../components/dashboard/NoOfEmployeeTab";
import RecentEntriesTab from "../../components/dashboard/RecentEntriesTab";
import AttendanceOverview from "../../components/dashboard/AttendanceOverview";
import AttendanceChart from "../../components/dashboard/AttendanceChart";
import WeeklyAttendanceChart from "../../components/dashboard/WeeklyAttendanceChart";
import { useAttendanceSummary, useWeeklyAttendanceSummary, useRecentEntries } from "../../services/reportService";
import { useGetBranches } from "../../services/companyService";
import { useUserContext } from "../../context/UserContext";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import { useCountrySelection } from "../../hooks/useCountrySelection";
import CountrySelectionModal from "../../components/CountrySelectionModal";
import { FiUsers, FiUserCheck, FiUserX, FiClock, FiCalendar, FiLock, FiStar } from "react-icons/fi";
import EmployeeUsageCard from "../../components/EmployeeUsageCard";
import PlanInfoCard from "../../components/PlanInfoCard";
import { processAttendanceData, processWeeklyAttendanceData } from "../../utils/dashboardDataProcessor";
import AIAnalyticsDashboard from "../../components/dashboard/AIAnalyticsDashboard";
import BranchAccessRestriction from "../../components/BranchAccessRestriction";
import BranchFilter from "../../components/BranchFilter";
import BranchStatusSummary from "../../components/BranchStatusSummary";
import DashboardFilterBar from "../../components/DashboardFilterBar";

const Dashboard = () => {
  const [viewMode, setViewMode] = useState('standard');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState("all");
  
  const { canAccessFeature, showFeatureRestriction } = usePlanRestriction();
  
  // Country selection logic - appears immediately after login
  const {
    showCountryModal,
    closeCountryModal,
    handleCountrySelectionSuccess,
    planInfo,
    needsCountrySelection
  } = useCountrySelection();
  
  const { recentEntriesData, recentEntriesIsLoading } = useRecentEntries({
    enabled: true,
    branch_id: selectedBranch
  });
  const {
    attendanceSummaryIsLoading,
    attendanceSummaryData,
  } = useAttendanceSummary({ 
    enabled: true,
    branch_id: selectedBranch
  });

  const {
    weeklyAttendanceSummaryIsLoading,
    weeklyAttendanceSummaryData,
  } = useWeeklyAttendanceSummary({ 
    enabled: true,
    branch_id: selectedBranch
  });

  const { setDashboardState, user } = useUserContext();
  const isCompanyAdmin = user?.role === 'company_admin';
  
  // Get branches data for company admins
  const { branches: branchesData, branchesIsLoading, refetchBranches } = useGetBranches();

  // Process data once for all components
  const processedAttendanceData = processAttendanceData(attendanceSummaryData);
  const processedWeeklyData = processWeeklyAttendanceData(weeklyAttendanceSummaryData);

  // Set dashboard state when component mounts
  useEffect(() => {
    setDashboardState(true);
    
    // Auto-refresh dashboard data after login
    const isFreshLogin = sessionStorage.getItem('freshLogin');
    if (isFreshLogin) {
      // Clear the flag
      sessionStorage.removeItem('freshLogin');
      
      // Trigger a refresh after a short delay to ensure all context is loaded
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 1000);
    }
    
    // Cleanup when component unmounts
    return () => {
      setDashboardState(false);
    };
  }, [setDashboardState]);

  return (
    <>
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch" mb={8}>
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Dashboard
            </Heading>
            <Text color="gray.600" fontSize="md">
              {viewMode === 'standard' 
                ? 'Overview of employee attendance and recent activities'
                : 'AI-powered insights and predictive analytics'
              }
            </Text>
          </Box>
          
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button 
              onClick={() => setViewMode('standard')}
              colorScheme={viewMode === 'standard' ? 'blue' : 'gray'}
              variant={viewMode === 'standard' ? 'solid' : 'outline'}
            >
              Standard View
            </Button>
            <Button 
              onClick={() => setViewMode('ai')}
              colorScheme={viewMode === 'ai' ? 'blue' : 'gray'}
              variant={viewMode === 'ai' ? 'solid' : 'outline'}
            >
              AI Analytics
            </Button>
          </ButtonGroup>
        </HStack>
      </VStack>



             {/* Conditional Rendering based on view mode */}
       {viewMode === 'standard' ? (
         <>
           {/* Employee Usage and Plan Info Cards */}
           <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
             <EmployeeUsageCard />
             <PlanInfoCard />
           </SimpleGrid>
           
                       {/* Compact Filter Bar positioned above Branch Summary */}
            {isCompanyAdmin && (
              <Box mb={4}>
                <DashboardFilterBar
                  selectedBranch={selectedBranch}
                  onBranchChange={setSelectedBranch}
                  branches={branchesData || []}
                  totalEmployees={branchesData ? branchesData.reduce((sum, branch) => sum + (branch.total_employees || 0), 0) : 0}
                  onRefresh={() => {
                    refetchBranches();
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  isLoading={branchesIsLoading}
                />
              </Box>
            )}
            
            {/* Branch Status Summary for Company Admins */}
            {isCompanyAdmin && (
              <Box mb={8}>
                <BranchStatusSummary branches={branchesData || []} />
              </Box>
            )}
         </>
               ) : (
          isCompanyAdmin ? (
            <>
              {/* Professional Filter Bar for AI Analytics */}
              <DashboardFilterBar
                selectedBranch={selectedBranch}
                onBranchChange={setSelectedBranch}
                branches={branchesData || []}
                totalEmployees={branchesData ? branchesData.reduce((sum, branch) => sum + (branch.total_employees || 0), 0) : 0}
                onRefresh={() => {
                  refetchBranches();
                  setRefreshTrigger(prev => prev + 1);
                }}
                isLoading={branchesIsLoading}
              />
              <AIAnalyticsDashboard selectedBranch={selectedBranch} />
            </>
          ) : (
            <BranchAccessRestriction featureName="AI Analytics" />
          )
        )}

              {/* Standard Dashboard Content */}
        {viewMode === 'standard' && (
          <>
            {/* Stats Cards */}
            {attendanceSummaryIsLoading ? (
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={6} mb={8}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box key={i} bg="white" p={6} borderRadius="16px" shadow="sm" border="1px solid" borderColor="gray.100">
                    <Skeleton height="20px" mb={2} />
                    <Skeleton height="32px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              Object.keys(attendanceSummaryData).length > 0 && (
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={6} mb={8}>
                  {processedAttendanceData.statCards.map((stat, index) => (
                    <StatCard
                      key={index}
                      label={stat.label}
                      value={stat.value}
                      icon={
                        stat.icon === "FiUsers" ? <FiUsers /> :
                        stat.icon === "FiUserCheck" ? <FiUserCheck /> :
                        stat.icon === "FiUserX" ? <FiUserX /> :
                        stat.icon === "FiClock" ? <FiClock /> :
                        <FiCalendar />
                      }
                      color={stat.color}
                    />
                  ))}
                </SimpleGrid>
              )
            )}

            {/* Main Content */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
              {/* Employee Details */}
              <Box
                bg="white"
                borderRadius="16px"
                p={6}
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.900" fontWeight="600">
                    Employee Details
                  </Heading>
                  {attendanceSummaryIsLoading ? (
                    <Stack>
                      <Skeleton height="30px" />
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} height="60px" />
                      ))}
                    </Stack>
                  ) : (
                    Object.keys(attendanceSummaryData).length > 0 && (
                      <NoOfEmployeeTab
                        totalEmp={processedAttendanceData.totalEmployees}
                        absent={processedAttendanceData.absentCount}
                        present={processedAttendanceData.presentCount}
                        lateComers={processedAttendanceData.lateCount}
                        leave={processedAttendanceData.leaveCount}
                        data={attendanceSummaryData}
                      />
                    )
                  )}
                </VStack>
              </Box>

              {/* Recent Entries */}
              <Box
                bg="white"
                borderRadius="16px"
                p={6}
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.900" fontWeight="600">
                    Recent Entries
                  </Heading>
                  {recentEntriesIsLoading ? (
                    <Stack>
                      <Skeleton height="30px" />
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} height="80px" />
                      ))}
                    </Stack>
                  ) : (
                    !recentEntriesData.length ? (
                      <Text color="gray.500" textAlign="center" py={8}>
                        {recentEntriesData?.detail || "No recent entries"}
                      </Text>
                    ) : (
                      <RecentEntriesTab
                        entries={recentEntriesData}
                        isLoading={recentEntriesIsLoading}
                      />
                    )
                  )}
                </VStack>
              </Box>
            </SimpleGrid>

            {/* Attendance Overview Section */}
            <Box
              bg="white"
              borderRadius="16px"
              p={6}
              shadow="sm"
              border="1px solid"
              borderColor="gray.100"
              mt={8}
            >
              <AttendanceOverview 
                attendanceData={processedAttendanceData}
                weeklyData={processedWeeklyData}
                isLoading={attendanceSummaryIsLoading}
                weeklyIsLoading={weeklyAttendanceSummaryIsLoading}
              />
            </Box>

            {/* Attendance Charts Section */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mt={8}>
              <Box
                bg="white"
                borderRadius="16px"
                p={6}
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <AttendanceChart 
                  data={processedAttendanceData}
                  isLoading={attendanceSummaryIsLoading}
                  chartType="pie"
                />
              </Box>
              <Box
                bg="white"
                borderRadius="16px"
                p={6}
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <AttendanceChart 
                  data={processedAttendanceData}
                  isLoading={attendanceSummaryIsLoading}
                  chartType="bar"
                />
              </Box>
            </SimpleGrid>

            {/* Weekly Attendance Trends Section */}
            <Box
              bg="white"
              borderRadius="16px"
              p={6}
              shadow="sm"
              border="1px solid"
              borderColor="gray.100"
              mt={8}
            >
              <WeeklyAttendanceChart 
                data={processedWeeklyData}
                isLoading={weeklyAttendanceSummaryIsLoading}
              />
            </Box>
          </>
        )}
    </Box>

    {/* Country Selection Modal - appears immediately after login */}
    <CountrySelectionModal
      isOpen={showCountryModal}
      onClose={closeCountryModal}
      onSuccess={handleCountrySelectionSuccess}
    />
    </>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", iconBg: "blue.100" },
    green: { bg: "green.50", color: "green.600", iconBg: "green.100" },
    red: { bg: "red.50", color: "red.600", iconBg: "red.100" },
    orange: { bg: "orange.50", color: "orange.600", iconBg: "orange.100" },
    purple: { bg: "purple.50", color: "purple.600", iconBg: "purple.100" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.100"
      _hover={{ transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg={colors.iconBg}
          color={colors.color}
        >
          {icon}
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {label}
          </Text>
          <Text fontSize="2xl" fontWeight="700" color="gray.900">
            {value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default Dashboard;
